import { DIA_BOOT_THEME_EXCLUDED_PATHS } from '../../advanced-cobranding/dia/bootRoutes';

const BOOT_THEME_STYLE_ID = 'cobranding-boot-theme';

type BootTheme = {
  light: string;
  dark: string;
  excludePaths?: readonly string[];
};

// Experiences opt into bootstrap theming here. Dia is the only current opt-in;
// all other simple and advanced cobranding experiences remain untouched.
const BOOT_THEMES = {
  'dia--dia': { light: '#feffff', dark: '#000', excludePaths: DIA_BOOT_THEME_EXCLUDED_PATHS },
  'dia--windows': {
    light: '#f3f3f3',
    dark: '#202020',
    excludePaths: DIA_BOOT_THEME_EXCLUDED_PATHS,
  },
} as const satisfies Record<string, BootTheme>;

const hasBootTheme = (application: string): application is keyof typeof BOOT_THEMES =>
  Object.prototype.hasOwnProperty.call(BOOT_THEMES, application);

const stripTrailingSlash = (pathname: string): string =>
  pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;

const isExcludedPath = (pathname: string, excludePaths: readonly string[]): boolean => {
  const path = stripTrailingSlash(pathname);
  return excludePaths.some(prefix => path === prefix || path.startsWith(prefix + '/'));
};

/** Applies an opted-in cobranding document theme before React renders. */
export function applyCobrandingBootTheme(): void {
  if (typeof document === 'undefined') return;

  let application: string | null = null;
  try {
    application = new URLSearchParams(window.location.search).get('application');
  } catch {
    return;
  }

  if (!application || !hasBootTheme(application)) return;
  const theme = BOOT_THEMES[application];
  if (document.getElementById(BOOT_THEME_STYLE_ID)) return;

  if (theme.excludePaths && isExcludedPath(window.location.pathname, theme.excludePaths)) return;

  document.documentElement.style.colorScheme = 'light dark';

  const style = document.createElement('style');
  style.id = BOOT_THEME_STYLE_ID;
  style.textContent = [
    `:root { --cobranding-loading-overlay-bg: ${theme.light}; }`,
    `@media (prefers-color-scheme: dark) { :root { --cobranding-loading-overlay-bg: ${theme.dark}; } }`,
    'html, body { background: var(--cobranding-loading-overlay-bg) !important; }',
  ].join('\n');
  document.head.appendChild(style);
}
